/**
 * RecurringWorkOrdersPage - List view for Work Order recurring schedules.
 *
 * Thin page wrapper around the shared RepeatsListTemplate — supplies the
 * type filter, the create-CTA route, and the detail-page route builder.
 * The matching InspectionsPage variant lives next door and reuses the
 * same template so the two surfaces stay visually identical.
 *
 * Access: admin / super_admin only (gated in App.tsx routing; the API
 * also rejects unauthorized writes).
 *
 * @route /recurring-work-orders
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { recurrenceSeriesService } from '../../services/recurrenceSeriesService';
import type { RecurrenceSeriesRecord } from '../../services/recurrenceSeriesService';
import { RepeatsListTemplate } from '../../components/templates/RepeatsListTemplate';
import { qk } from '../../lib/queryKeys';

const RecurringWorkOrdersPage: React.FC = () => {
	const { hasAnyRole } = useAuth();

	const canCreate = hasAnyRole(['super_admin', 'admin']);

	const params = { type: 'work_order' };
	const query = useQuery({
		queryKey: qk.recurrenceSeries.list(params),
		queryFn: async () => {
			const res = await recurrenceSeriesService.list('work_order');
			return ((res.data as any)?.data ?? []) as RecurrenceSeriesRecord[];
		},
	});

	const series = query.data ?? [];
	const isLoading = query.isLoading;
	const error = query.error ? (query.error as Error).message : null;

	return (
		<RepeatsListTemplate
			className="recurring-work-orders-page"
			dataRef="page-recurring-work-orders-page"
			title="Recurring Work Orders"
			subtitle="Google-Calendar-style schedules that materialize work orders ahead of time."
			createLabel="+ New Recurring Work Order"
			createRoute="/work-orders/create"
			canCreate={canCreate}
			detailRoute={(id) => `/recurring-work-orders/${id}`}
			series={series}
			isLoading={isLoading}
			error={error}
			emptyMessage={canCreate
				? 'No recurring work orders yet. Create a work order and pick a Frequency to start a recurring schedule.'
				: 'No recurring work orders assigned to your company yet.'}
		/>
	);
};

export default RecurringWorkOrdersPage;
